import { useMediaQuery, useTheme } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Loading from "../../../components/Loading";
import NoData from "../../../components/UI/NoData";
import useFetch from "../../../hooks/useFetch";
import { ScheduleAppointmentService } from "../appointment.service";
import { IScheduleCheckInRule } from "../model";
import ScheduleList from "./NewScheduleList";
import {
  getMyAppointments,
  getMySchedulesPortal,
} from "../../../services/mySchedulesPortal.service";
import { INewScheduleProps, INewSchedulesProps } from "./newModels";
import SchedulesTable from "./NewSchedulesTable";

const NewSchedules: React.FC<INewSchedulesProps> = ({
  historic = false,
  isLinkAccess = false,
  basePath = "/schedule",
  allowedAction,
  ticketAutomationReceivedRuleId,
}) => {
  const [schedules, setSchedules] = useState<INewScheduleProps>({
    isLinkAccess,
    data: [],
  });

  const [checkInRule, setCheckInRule] = useState<IScheduleCheckInRule | null>(
    null,
  );

  const { t } = useTranslation();

  const { sendRequest: scheduleAppointmentListRequest, loading: sLoading } =
    useFetch(ScheduleAppointmentService.getList);

  const { sendRequest: getMySchedulesPortalRequest, loading: gLoading } =
    useFetch(getMySchedulesPortal);

  const { sendRequest: getMyAppointmentsRequest, loading: aLoading } =
    useFetch(getMyAppointments);

  const { breakpoints } = useTheme();

  const mobile = useMediaQuery(breakpoints.down("md"));
  const useNewScheduleAppointments = basePath === "/schedule-new";

  const fetchScheduleAppointmentList = useCallback(async () => {
    if (useNewScheduleAppointments) {
      const { data, success } = await getMyAppointmentsRequest({
        historic,
        showFamilyGroup: true,
        ticketAutomationReceivedRuleId,
      });

      if (data && success) {
        setSchedules({
          isLinkAccess: false,
          data: data.map((schedule) => ({
            identifier: schedule.identifier!,
            pacientName: schedule.pacientName ?? "",
            calendarIdentifier: schedule.calendarIdentifier ?? 0,
            descriptionSite: schedule.descriptionSite ?? "",
            tasyScheduleTypeCode: schedule.tasyScheduleTypeCode,
            tasyScheduleTypeName: schedule.tasyScheduleTypeName,
            procedureName: schedule.procedureName,
            physicianNameExecute: schedule.physicianNameExecute,
            calendarName: schedule.calendarName,
            calendarStartDate: schedule.calendarStartDate ?? "",
            calendarStatusCode: schedule.calendarStatusCode ?? "",
            calendarStatusName: schedule.calendarStatusName ?? "",
            calendarDate: schedule.calendarDate ?? "",
            roomIdentifier: schedule.roomIdentifier ?? 0,
            roomName: schedule.roomName ?? null,
            encounterIdentifier: schedule.encounterIdentifier ?? 0,
            tasySchedulingType: {
              code: schedule.tasyScheduleTypeCode?.toString(),
              name: schedule.tasyScheduleTypeName,
            } as any,
          })),
        });
        setCheckInRule(null);
      }

      return;
    }

    const { data, success } = await scheduleAppointmentListRequest(historic);

    if (data && success) {
      setSchedules({
        isLinkAccess: false,
        data: data.calendarConsultationExamScheduleAppointment,
      });
      setCheckInRule(data.tasySchedCheckInRule);
    }
  }, [
    getMyAppointmentsRequest,
    historic,
    ticketAutomationReceivedRuleId,
    scheduleAppointmentListRequest,
    useNewScheduleAppointments,
  ]);

  const fetchMySchedulesPortalRequest = useCallback(async () => {
    const { data, success } = await getMySchedulesPortalRequest({ historic });

    if (data && success) {
      setSchedules({
        isLinkAccess: true,
        data: data,
      });
    }
  }, [getMySchedulesPortalRequest, historic]);

  useEffect(() => {
    if (isLinkAccess) {
      fetchMySchedulesPortalRequest();
    } else {
      fetchScheduleAppointmentList();
    }
  }, [
    isLinkAccess,
    fetchMySchedulesPortalRequest,
    fetchScheduleAppointmentList,
  ]);

  const loading = sLoading || gLoading || aLoading;

  if (loading) {
    return <Loading />;
  }

  if (schedules.data.length === 0) {
    return <NoData message={t("page.schedules.home.noFound")} />;
  }

  if (mobile) {
    return (
      <ScheduleList
        schedules={schedules}
        checkInRule={checkInRule!}
        isHistoric={historic}
        basePath={basePath}
        allowedAction={allowedAction}
      />
    );
  }

  return (
    <SchedulesTable
      schedules={schedules}
      checkInRule={checkInRule!}
      isHistoric={historic}
      basePath={basePath}
      allowedAction={allowedAction}
    />
  );
};

export default memo(NewSchedules);
